// src/components/renderers/AboutSectionRenderer.jsx
import React, { useState, useEffect } from 'react';
import {
  FileText,
  Image as ImageIcon,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Target,
  Heart,
  Lightbulb,
  Award,
  Users,
  Briefcase,
  Building,
  Sparkles,
  Star,
  Zap,
  TrendingUp,
  Shield,
  Globe,
  Layers,
  ArrowRight,
  Check,
  Play,
  ExternalLink
} from 'lucide-react';
import { MathRenderer } from '../math/MathRenderer';

// ============================================================================
// ICON MAPPING
// ============================================================================

const SECTION_ICONS = {
  bio: Users,
  story: Heart,
  mission: Target,
  vision: Lightbulb,
  values: Award,
  expertise: Briefcase,
  overview: Building,
  history: FileText,
  team: Users,
  description: FileText,
  purpose: Target,
  highlights: Sparkles,
  services: Briefcase,
  whyUs: Award,
  commitment: Shield,
  accreditation: Award
};

// ============================================================================
// VISUAL THEMES FOR DIFFERENT SECTIONS
// ============================================================================

const SECTION_THEMES = {
  mission: {
    gradient: 'from-blue-500 via-blue-600 to-indigo-600',
    bgGradient: 'from-blue-50 via-indigo-50 to-purple-50',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    accentColor: '#3b82f6'
  },
  vision: {
    gradient: 'from-purple-500 via-purple-600 to-pink-600',
    bgGradient: 'from-purple-50 via-pink-50 to-rose-50',
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    accentColor: '#9333ea'
  },
  values: {
    gradient: 'from-emerald-500 via-teal-600 to-cyan-600',
    bgGradient: 'from-emerald-50 via-teal-50 to-cyan-50',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    accentColor: '#10b981'
  },
  story: {
    gradient: 'from-rose-500 via-pink-600 to-purple-600',
    bgGradient: 'from-rose-50 via-pink-50 to-purple-50',
    iconBg: 'bg-rose-100',
    iconColor: 'text-rose-600',
    accentColor: '#f43f5e'
  },
  expertise: {
    gradient: 'from-orange-500 via-amber-600 to-yellow-600',
    bgGradient: 'from-orange-50 via-amber-50 to-yellow-50',
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
    accentColor: '#f97316'
  },
  default: {
    gradient: 'from-gray-500 via-gray-600 to-slate-600',
    bgGradient: 'from-gray-50 via-slate-50 to-zinc-50',
    iconBg: 'bg-gray-100',
    iconColor: 'text-gray-600',
    accentColor: '#64748b'
  }
};

// ============================================================================
// MODERN SECTION CARD COMPONENT
// ============================================================================

const ModernSectionCard = ({ 
  sectionKey, 
  sectionData, 
  IconComponent, 
  theme,
  customStyling,
  onReadMore 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showFull, setShowFull] = useState(false);

  const shouldTruncate = sectionData.content?.length > 400;
  const displayContent = shouldTruncate && !showFull
    ? sectionData.content.substring(0, 400) + '...'
    : sectionData.content;

  return (
    <div
      className="group relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Animated background gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br ${theme.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-3xl`} />
      
      {/* Main card */}
      <div className="relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 overflow-hidden">
        {/* Decorative corner element */}
        <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${theme.gradient} opacity-10 blur-3xl transform translate-x-16 -translate-y-16 group-hover:scale-150 transition-transform duration-700`} />
        
        {/* Icon with animated ring */}
        <div className="relative mb-6">
          <div className={`absolute inset-0 ${theme.iconBg} rounded-2xl blur-xl opacity-0 group-hover:opacity-60 transition-opacity duration-500`} />
          <div className={`relative w-16 h-16 ${theme.iconBg} rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}>
            <IconComponent className={`h-8 w-8 ${theme.iconColor}`} />
          </div>
        </div>

        {/* Title */}
        {sectionData.title && (
          <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-gray-900 group-hover:to-gray-600 transition-all duration-300">
            {sectionData.title}
          </h3>
        )}

        {/* Content */}
        <div className="prose prose-lg max-w-none mb-6">
          <MathRenderer
            content={displayContent}
            className="text-gray-600 leading-relaxed"
            enableMarkdown={true}
          />
        </div>

        {/* Read more button */}
        {shouldTruncate && (
          <button
            onClick={() => {
              setShowFull(!showFull);
              onReadMore?.(sectionKey);
            }}
            className={`inline-flex items-center gap-2 text-sm font-semibold ${theme.iconColor} hover:gap-3 transition-all duration-300`}
          >
            {showFull ? (
              <>
                Show Less
                <ChevronUp className="h-4 w-4" />
              </>
            ) : (
              <>
                Read More
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>
        )}

        {/* Highlights */}
        {sectionData.highlights && sectionData.highlights.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-100">
            <div className="space-y-3">
              {sectionData.highlights.map((highlight, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 group/item"
                  style={{ animationDelay: `${idx * 100}ms` }}
                >
                  <div className={`mt-1 w-5 h-5 ${theme.iconBg} rounded-full flex items-center justify-center flex-shrink-0 group-hover/item:scale-110 transition-transform`}>
                    <Check className={`h-3 w-3 ${theme.iconColor}`} />
                  </div>
                  <span className="text-gray-700 group-hover/item:text-gray-900 transition-colors">
                    {highlight}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bottom accent line */}
        <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${theme.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`} />
      </div>
    </div>
  );
};

// ============================================================================
// HERO STYLE SECTION
// ============================================================================

const HeroStyleSection = ({ 
  sectionKey, 
  sectionData, 
  IconComponent, 
  theme,
  customStyling 
}) => {
  return (
    <div className="relative overflow-hidden rounded-3xl">
      {/* Background with animated gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br ${theme.gradient}`}>
        <div className="absolute inset-0 bg-black/10" />
        {/* Animated circles */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      {/* Content */}
      <div className="relative z-10 p-12 lg:p-16">
        <div className="max-w-4xl">
          {/* Icon */}
          <div className="mb-8">
            <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <IconComponent className="h-10 w-10 text-white" />
            </div>
          </div>

          {/* Title */}
          {sectionData.title && (
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              {sectionData.title}
            </h2>
          )}

          {/* Content */}
          <div className="prose prose-lg prose-invert max-w-none">
            <MathRenderer
              content={sectionData.content}
              className="text-white/90 text-lg leading-relaxed"
              enableMarkdown={true}
            />
          </div>

          {/* Highlights as tags */}
          {sectionData.highlights && sectionData.highlights.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-3">
              {sectionData.highlights.map((highlight, idx) => (
                <span
                  key={idx}
                  className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium hover:bg-white/30 transition-colors cursor-default"
                >
                  {highlight}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Decorative bottom wave */}
      <svg
        className="absolute bottom-0 left-0 w-full"
        viewBox="0 0 1440 120"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M0 0L60 10C120 20 240 40 360 46.7C480 53 600 47 720 43.3C840 40 960 40 1080 46.7C1200 53 1320 67 1380 73.3L1440 80V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V0Z"
          fill="white"
          fillOpacity="0.1"
        />
      </svg>
    </div>
  );
};

// ============================================================================
// SPLIT LAYOUT WITH IMAGE
// ============================================================================

const SplitLayoutSection = ({ 
  sectionKey, 
  sectionData, 
  IconComponent, 
  theme,
  customStyling,
  imagePosition = 'right'
}) => {
  const hasMedia = sectionData.media && sectionData.media.length > 0;
  const image = hasMedia ? sectionData.media[0] : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      {/* Text content */}
      <div className={imagePosition === 'right' ? 'order-1' : 'order-1 lg:order-2'}>
        {/* Icon badge */}
        <div className="inline-flex items-center gap-3 mb-6">
          <div className={`w-12 h-12 ${theme.iconBg} rounded-xl flex items-center justify-center`}>
            <IconComponent className={`h-6 w-6 ${theme.iconColor}`} />
          </div>
          <span className={`text-sm font-semibold ${theme.iconColor} uppercase tracking-wider`}>
            {sectionKey}
          </span>
        </div>

        {/* Title */}
        {sectionData.title && (
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            {sectionData.title}
          </h2>
        )}

        {/* Content */}
        <div className="prose prose-lg max-w-none mb-8">
          <MathRenderer
            content={sectionData.content}
            className="text-gray-600 leading-relaxed text-lg"
            enableMarkdown={true}
          />
        </div>

        {/* Highlights with custom styling */}
        {sectionData.highlights && sectionData.highlights.length > 0 && (
          <div className="space-y-4">
            {sectionData.highlights.map((highlight, idx) => (
              <div
                key={idx}
                className="flex items-start gap-4 p-4 bg-gradient-to-r from-gray-50 to-transparent rounded-xl hover:from-gray-100 transition-colors"
              >
                <div className={`mt-0.5 w-6 h-6 bg-gradient-to-br ${theme.gradient} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <Check className="h-4 w-4 text-white" />
                </div>
                <span className="text-gray-700 font-medium">{highlight}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Image */}
      <div className={imagePosition === 'right' ? 'order-2' : 'order-2 lg:order-1'}>
        {image ? (
          <div className="relative group">
            {/* Decorative background */}
            <div className={`absolute -inset-4 bg-gradient-to-br ${theme.gradient} rounded-3xl opacity-20 blur-2xl group-hover:opacity-30 transition-opacity`} />
            
            {/* Image */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={image.url}
                alt={image.caption || sectionData.title}
                className="w-full h-auto object-cover transform group-hover:scale-105 transition-transform duration-700"
              />
              {image.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                  <p className="text-white text-sm">{image.caption}</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className={`relative rounded-3xl overflow-hidden bg-gradient-to-br ${theme.bgGradient} p-12 shadow-xl`}>
            <div className="flex items-center justify-center h-64">
              <IconComponent className="h-32 w-32 text-gray-300" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// TIMELINE STYLE LAYOUT
// ============================================================================

const TimelineSection = ({ 
  sectionKey, 
  sectionData, 
  IconComponent, 
  theme,
  index 
}) => {
  return (
    <div className="relative pl-8 lg:pl-12">
      {/* Timeline line */}
      <div className={`absolute left-0 top-0 bottom-0 w-0.5 bg-gradient-to-b ${theme.gradient}`} />
      
      {/* Timeline dot */}
      <div className={`absolute left-0 top-8 -ml-2 w-4 h-4 rounded-full bg-gradient-to-br ${theme.gradient}`}>
        <div className="absolute inset-0 rounded-full bg-white animate-ping opacity-75" />
      </div>

      {/* Content card */}
      <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100 mb-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-4">
          <div className={`w-12 h-12 ${theme.iconBg} rounded-xl flex items-center justify-center`}>
            <IconComponent className={`h-6 w-6 ${theme.iconColor}`} />
          </div>
          {sectionData.title && (
            <h3 className="text-2xl font-bold text-gray-900">
              {sectionData.title}
            </h3>
          )}
        </div>

        {/* Content */}
        <div className="prose max-w-none">
          <MathRenderer
            content={sectionData.content}
            className="text-gray-600"
            enableMarkdown={true}
          />
        </div>

        {/* Highlights */}
        {sectionData.highlights && sectionData.highlights.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-100">
            <div className="flex flex-wrap gap-2">
              {sectionData.highlights.map((highlight, idx) => (
                <span
                  key={idx}
                  className={`px-3 py-1 ${theme.iconBg} ${theme.iconColor} text-sm rounded-full font-medium`}
                >
                  {highlight}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// GLASSMORPHISM CARD
// ============================================================================

const GlassmorphismCard = ({ 
  sectionKey, 
  sectionData, 
  IconComponent, 
  theme 
}) => {
  return (
    <div className="relative">
      {/* Background blur element */}
      <div className={`absolute inset-0 bg-gradient-to-br ${theme.gradient} opacity-30 blur-3xl -z-10`} />
      
      {/* Glass card */}
      <div className="relative backdrop-blur-xl bg-white/70 border border-white/20 rounded-3xl p-8 shadow-2xl hover:bg-white/80 transition-all duration-500">
        {/* Floating icon */}
        <div className="absolute -top-6 left-8">
          <div className={`w-16 h-16 bg-gradient-to-br ${theme.gradient} rounded-2xl flex items-center justify-center shadow-xl transform hover:rotate-12 transition-transform`}>
            <IconComponent className="h-8 w-8 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="mt-8">
          {sectionData.title && (
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              {sectionData.title}
            </h3>
          )}

          <div className="prose prose-lg max-w-none mb-6">
            <MathRenderer
              content={sectionData.content}
              className="text-gray-700 leading-relaxed"
              enableMarkdown={true}
            />
          </div>

          {/* Highlights grid */}
          {sectionData.highlights && sectionData.highlights.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              {sectionData.highlights.map((highlight, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-3 p-3 bg-white/50 backdrop-blur-sm rounded-xl"
                >
                  <Sparkles className={`h-5 w-5 ${theme.iconColor}`} />
                  <span className="text-sm text-gray-700 font-medium">
                    {highlight}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Decorative corner gradients */}
        <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${theme.gradient} opacity-10 blur-2xl rounded-full`} />
        <div className={`absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr ${theme.gradient} opacity-10 blur-2xl rounded-full`} />
      </div>
    </div>
  );
};

// ============================================================================
// BENTO GRID LAYOUT
// ============================================================================

const BentoGridLayout = ({ sections, customStyling, onInteraction }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-auto">
      {sections.map(([sectionKey, sectionData], index) => {
        const IconComponent = SECTION_ICONS[sectionKey] || FileText;
        const theme = SECTION_THEMES[sectionKey] || SECTION_THEMES.default;
        const hasLongContent = sectionData.content?.length > 300;
        
        // Vary sizes for visual interest
        const spanClass = 
          index === 0 ? 'md:col-span-2 md:row-span-2' :
          hasLongContent ? 'md:col-span-2' :
          'md:col-span-1';

        return (
          <div key={sectionKey} className={spanClass}>
            <div className={`h-full bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 group relative overflow-hidden ${
              index === 0 ? 'bg-gradient-to-br from-gray-50 to-white' : ''
            }`}>
              {/* Decorative element */}
              <div className={`absolute top-0 right-0 w-40 h-40 bg-gradient-to-br ${theme.gradient} opacity-5 blur-3xl transform translate-x-20 -translate-y-20 group-hover:scale-150 transition-transform duration-700`} />
              
              {/* Icon */}
              <div className={`relative mb-4 inline-flex w-14 h-14 ${theme.iconBg} rounded-2xl items-center justify-center transform group-hover:scale-110 group-hover:-rotate-6 transition-all duration-500`}>
                <IconComponent className={`h-7 w-7 ${theme.iconColor}`} />
              </div>

              {/* Title */}
              {sectionData.title && (
                <h3 className={`text-xl font-bold text-gray-900 mb-3 ${index === 0 ? 'text-2xl' : ''}`}>
                  {sectionData.title}
                </h3>
              )}

              {/* Content */}
              <div className="prose prose-sm max-w-none">
                <MathRenderer
                  content={sectionData.content}
                  className="text-gray-600 leading-relaxed"
                  enableMarkdown={true}
                />
              </div>

              {/* Highlights */}
              {sectionData.highlights && sectionData.highlights.length > 0 && (
                <div className="mt-4 space-y-2">
                  {sectionData.highlights.slice(0, 3).map((highlight, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${theme.gradient}`} />
                      <span className="text-sm text-gray-600">{highlight}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============================================================================
// MAIN RENDERER WITH LAYOUT OPTIONS
// ============================================================================

export const AboutSectionRenderer = ({
  data,
  bookData,
  customStyling = {},
  onInteraction
}) => {
  const [activeLayout, setActiveLayout] = useState('modern-cards');

  if (!data) return null;

  // Get enabled sections
  const sections = Object.entries(data)
    .filter(([key, sectionData]) => {
      if (['showDividers', 'enableReadMore'].includes(key)) return false;
      return sectionData?.enabled !== false && sectionData?.content?.trim();
    })
    .sort((a, b) => (a[1].order || 999) - (b[1].order || 999));

  if (sections.length === 0) return null;

  // Detect layout preference from data
  const layoutPreference = data.layoutStyle || bookData?.aboutSection?.layoutStyle || 'modern-cards';

  const renderLayout = () => {
    switch (layoutPreference) {
      case 'bento-grid':
        return <BentoGridLayout sections={sections} customStyling={customStyling} onInteraction={onInteraction} />;

      case 'timeline':
        return (
          <div className="space-y-8">
            {sections.map(([sectionKey, sectionData], index) => {
              const IconComponent = SECTION_ICONS[sectionKey] || FileText;
              const theme = SECTION_THEMES[sectionKey] || SECTION_THEMES.default;
              return (
                <TimelineSection
                  key={sectionKey}
                  sectionKey={sectionKey}
                  sectionData={sectionData}
                  IconComponent={IconComponent}
                  theme={theme}
                  index={index}
                />
              );
            })}
          </div>
        );

      case 'hero-style':
        return (
          <div className="space-y-12">
            {sections.map(([sectionKey, sectionData], index) => {
              const IconComponent = SECTION_ICONS[sectionKey] || FileText;
              const theme = SECTION_THEMES[sectionKey] || SECTION_THEMES.default;
              
              if (index === 0) {
                return (
                  <HeroStyleSection
                    key={sectionKey}
                    sectionKey={sectionKey}
                    sectionData={sectionData}
                    IconComponent={IconComponent}
                    theme={theme}
                    customStyling={customStyling}
                  />
                );
              } else {
                return (
                  <ModernSectionCard
                    key={sectionKey}
                    sectionKey={sectionKey}
                    sectionData={sectionData}
                    IconComponent={IconComponent}
                    theme={theme}
                    customStyling={customStyling}
                    onReadMore={onInteraction}
                  />
                );
              }
            })}
          </div>
        );

      case 'split-layout':
        return (
          <div className="space-y-24">
            {sections.map(([sectionKey, sectionData], index) => {
              const IconComponent = SECTION_ICONS[sectionKey] || FileText;
              const theme = SECTION_THEMES[sectionKey] || SECTION_THEMES.default;
              return (
                <SplitLayoutSection
                  key={sectionKey}
                  sectionKey={sectionKey}
                  sectionData={sectionData}
                  IconComponent={IconComponent}
                  theme={theme}
                  customStyling={customStyling}
                  imagePosition={index % 2 === 0 ? 'right' : 'left'}
                />
              );
            })}
          </div>
        );

      case 'glassmorphism':
        return (
          <div className="space-y-12">
            {sections.map(([sectionKey, sectionData]) => {
              const IconComponent = SECTION_ICONS[sectionKey] || FileText;
              const theme = SECTION_THEMES[sectionKey] || SECTION_THEMES.default;
              return (
                <GlassmorphismCard
                  key={sectionKey}
                  sectionKey={sectionKey}
                  sectionData={sectionData}
                  IconComponent={IconComponent}
                  theme={theme}
                />
              );
            })}
          </div>
        );

      case 'modern-cards':
      default:
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {sections.map(([sectionKey, sectionData]) => {
              const IconComponent = SECTION_ICONS[sectionKey] || FileText;
              const theme = SECTION_THEMES[sectionKey] || SECTION_THEMES.default;
              return (
                <ModernSectionCard
                  key={sectionKey}
                  sectionKey={sectionKey}
                  sectionData={sectionData}
                  IconComponent={IconComponent}
                  theme={theme}
                  customStyling={customStyling}
                  onReadMore={onInteraction}
                />
              );
            })}
          </div>
        );
    }
  };

  return (
    <div className="about-section-renderer py-12">
      {renderLayout()}

      {/* Global styles */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }

        @keyframes pulse-slow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        .animate-float {
          animation: float 3s ease-in-out infinite;
        }

        .backdrop-blur-xl {
          backdrop-filter: blur(20px);
        }
      `}</style>
    </div>
  );
};

export default AboutSectionRenderer;